import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useStaffList } from '../../lib/staff/use-staff.js';
import { cn } from '@tailoring-crm/ui';

/**
 * "Assigned to": the staff member looking after a customer, or nobody.
 *
 * The roster offered is the shop's active owners and managers — the people who work the customer
 * book. A tailor cannot open a customer at all, so handing one to them would hand them to nobody.
 * The API is the side that checks; this only offers what it will accept.
 *
 * A customer already held by somebody who has since left the roster keeps that name on the
 * control (as `currentName`), so opening the form does not silently read as "Nobody".
 */
export function StaffAssigneeSelect({
  id,
  value,
  onChange,
  currentName = null,
  disabled = false,
  className,
  ariaLabel,
}: {
  id?: string;
  /** A user id, or `''` for nobody. */
  value: string;
  onChange: (userId: string) => void;
  currentName?: string | null;
  disabled?: boolean;
  className?: string;
  ariaLabel?: string;
}): ReactElement {
  const { t } = useTranslation();
  const staff = useStaffList();
  const members = (staff.data?.items ?? []).filter(
    (member) => member.active && member.role !== 'tailor',
  );
  const missing = value !== '' && !members.some((member) => member.userId === value);

  return (
    <select
      id={id}
      value={value}
      disabled={disabled}
      aria-label={ariaLabel}
      onChange={(event) => onChange(event.target.value)}
      className={cn('h-9 rounded-md border border-input bg-background px-2 text-sm', className)}
    >
      <option value="">{t('customers.field.assignedToNobody')}</option>
      {missing ? <option value={value}>{currentName ?? value}</option> : null}
      {members.map((member) => (
        <option key={member.userId} value={member.userId}>
          {member.name}
        </option>
      ))}
    </select>
  );
}
